import { Link } from "react-router-dom";
import { ArrowUpRight, FileDown, Instagram, Phone } from "lucide-react";
import Logo from "./Logo";
import { CITIES, SITE, phoneDisplay } from "@/lib/site";
import { PROFILE_PDF_URL } from "@/lib/api";

const WhatsAppIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.2-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

export { WhatsAppIcon };

export default function Footer() {
  return (
    <footer className="relative border-t border-white/10 bg-obsidian-soft">
      <div className="container-x py-20 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo />
            <p className="mt-6 max-w-sm font-serif text-2xl italic leading-snug text-zinc-300">
              "The 3D you approve is the home you get."
            </p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-500">
              {SITE.name} — turnkey interior design and execution across Mumbai, Navi Mumbai, Thane, Pune, Delhi, Lucknow and Rishikesh.
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                { to: "/portfolio", label: "Portfolio" },
                { to: "/process", label: "The Process" },
                { to: "/about", label: "About Shriya" },
                { to: "/enquire", label: "Enquire" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-zinc-400 transition-colors hover:text-gold"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow">Cities</p>
            <ul className="mt-5 space-y-3 text-sm">
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/interiors/${c.slug}`}
                    data-testid={`footer-link-city-${c.slug}`}
                    className="text-zinc-400 transition-colors hover:text-gold"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow">Talk to us</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a
                  href={`tel:${SITE.phoneHref}`}
                  data-testid="footer-phone-link"
                  className="flex items-center gap-2 text-zinc-400 transition-colors hover:text-gold"
                >
                  <Phone className="h-3.5 w-3.5" /> {phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${SITE.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="flex items-center gap-2 text-zinc-400 transition-colors hover:text-wa"
                >
                  <WhatsAppIcon className="h-3.5 w-3.5" /> WhatsApp us
                </a>
              </li>
              <li>
                <a
                  href={SITE.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="flex items-center gap-2 text-zinc-400 transition-colors hover:text-gold"
                >
                  <Instagram className="h-3.5 w-3.5" /> {SITE.instagramHandle}
                </a>
              </li>
              <li>
                <a
                  href={PROFILE_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-download-profile"
                  className="flex items-center gap-2 text-zinc-400 transition-colors hover:text-gold"
                >
                  <FileDown className="h-3.5 w-3.5" /> Studio profile (PDF)
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="hairline mt-16" />
        <div className="mt-8 flex flex-col items-start justify-between gap-4 text-xs text-zinc-600 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <p className="flex items-center gap-2">
            Designed in 3D <span className="text-gold">·</span> Delivered in reality{" "}
            <ArrowUpRight className="h-3 w-3 text-gold" />
          </p>
        </div>
      </div>
    </footer>
  );
}
